import "./index.css";

import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

type CardType = {
  id: string;
  label: string;
  buildValue: number;
  stealValue: number;
  max?: number;
};

type DeckRow = {
  one: number;
  two: number;
  three: number;
  vp: number;
  gt: number;
  l: number;
  g: number;
  v: number;
  zero: number;
  points: number;
  stealsToWin: number;
};

const CARD_TYPES: CardType[] = [
  { id: "zero", label: "0", buildValue: 0, stealValue: 0 },
  { id: "one", label: "1", buildValue: 1, stealValue: 1 },
  { id: "L", label: "L", buildValue: 2, stealValue: 1, max: 3 },
  { id: "two", label: "2", buildValue: 2, stealValue: 2 },
  { id: "G", label: "G", buildValue: 3, stealValue: 2, max: 3 },
  { id: "three", label: "3", buildValue: 3, stealValue: 3 },
  { id: "V", label: "V", buildValue: 3, stealValue: 0, max: 3 },
  { id: "vp", label: "VP", buildValue: 4, stealValue: 4, max: 3 },
  { id: "gt", label: "GT", buildValue: 6, stealValue: 6, max: 1 },
];

const EMPTY_COUNTS = Object.fromEntries(CARD_TYPES.map((type) => [type.id, 0]));

const clampNonNegative = (value: number) => Math.max(0, Math.floor(value));
const clampWithin = (value: number, max: number) =>
  Math.max(0, Math.min(Math.floor(value), max));

const getTargetPoints = (deckSize: number) => {
  if (deckSize >= 40 && deckSize <= 44) return 18;
  if (deckSize >= 45 && deckSize <= 49) return 20;
  if (deckSize >= 50 && deckSize <= 54) return 22;
  if (deckSize >= 55 && deckSize <= 59) return 24;
  return null;
};

const expectedStealsToReach = (
  counts: number[],
  score: number,
  values: number[],
  target: number,
) => {
  const maxPossible =
    score +
    counts.reduce((total, count, index) => total + count * values[index], 0);
  if (maxPossible < target) return Infinity;

  const memo = new Map<string, number>();
  const helper = (currentCounts: number[], currentScore: number): number => {
    if (currentScore >= target) return 0;
    const key = `${currentScore}|${currentCounts.join(",")}`;
    const cached = memo.get(key);
    if (cached !== undefined) return cached;

    const totalCards = currentCounts.reduce((total, count) => total + count, 0);
    if (totalCards === 0) return Infinity;

    let expected = 1;
    let weighted = 0;
    currentCounts.forEach((count, index) => {
      if (count <= 0) return;
      const nextCounts = currentCounts.slice();
      nextCounts[index] -= 1;
      const nextScore = Math.min(target, currentScore + values[index]);
      weighted += (count / totalCards) * helper(nextCounts, nextScore);
    });
    expected += weighted;
    memo.set(key, expected);
    return expected;
  };

  return helper(counts, score);
};

export function App() {
  const [deckCounts, setDeckCounts] = useState<Record<string, number>>({
    ...EMPTY_COUNTS,
    one: 0,
    two: 4,
    three: 4,
    vp: 0,
    gt: 0,
    V: 0,
  });
  const [totalCards, setTotalCards] = useState(49);
  const [remainingCardsInput, setRemainingCardsInput] = useState(49);
  const [removedOne, setRemovedOne] = useState(0);
  const [removedTwo, setRemovedTwo] = useState(0);
  const [removedThree, setRemovedThree] = useState(0);
  const [removedFour, setRemovedFour] = useState(0);
  const [removedSix, setRemovedSix] = useState(0);
  const [removedVacheron, setRemovedVacheron] = useState(0);
  const [stolenOne, setStolenOne] = useState(0);
  const [stolenTwo, setStolenTwo] = useState(0);
  const [stolenThree, setStolenThree] = useState(0);
  const [stolenFour, setStolenFour] = useState(0);
  const [stolenSix, setStolenSix] = useState(0);
  const [stolenVacheron, setStolenVacheron] = useState(0);
  const [maturedVacheron, setMaturedVacheron] = useState(0);
  const [allowVP, setAllowVP] = useState(false);
  const [allowGT, setAllowGT] = useState(false);
  const [allowL, setAllowL] = useState(true);
  const [allowG, setAllowG] = useState(false);
  const [allowV, setAllowV] = useState(false);
  const [limitHighPointers, setLimitHighPointers] = useState(false);
  const [maxHighPointers, setMaxHighPointers] = useState(4);
  const [takePinned, setTakePinned] = useState(false);
  const [seeCount, setSeeCount] = useState(0);
  const [takeCount, setTakeCount] = useState(0);
  const [mcllCount, setMcllCount] = useState(0);
  const [clicksSpare, setClicksSpare] = useState(0);
  const [takeRequiresClick, setTakeRequiresClick] = useState(false);

  const nonZeroTotal = useMemo(
    () =>
      deckCounts.one +
      deckCounts.two +
      deckCounts.three +
      deckCounts.vp +
      deckCounts.gt,
    [
      deckCounts.gt,
      deckCounts.one,
      deckCounts.three,
      deckCounts.two,
      deckCounts.vp,
    ],
  );
  const rawZeroCount = useMemo(
    () => totalCards - nonZeroTotal,
    [totalCards, nonZeroTotal],
  );
  const rawTwoCount = useMemo(
    () => deckCounts.two - deckCounts.L,
    [deckCounts.two, deckCounts.L],
  );
  const rawThreeCount = useMemo(
    () => deckCounts.three - deckCounts.G - deckCounts.V,
    [deckCounts.G, deckCounts.V, deckCounts.three],
  );
  const zeroCount = useMemo(() => Math.max(0, rawZeroCount), [rawZeroCount]);
  const adjustedTwoCount = useMemo(
    () => Math.max(0, rawTwoCount),
    [rawTwoCount],
  );
  const adjustedThreeCount = useMemo(
    () => Math.max(0, rawThreeCount),
    [rawThreeCount],
  );
  const computedDeckCounts = useMemo(
    () => ({
      ...deckCounts,
      two: adjustedTwoCount,
      three: adjustedThreeCount,
      zero: zeroCount,
    }),
    [adjustedThreeCount, adjustedTwoCount, deckCounts, zeroCount],
  );

  const deckSize = useMemo(() => totalCards, [totalCards]);
  const targetPoints = useMemo(() => getTargetPoints(deckSize), [deckSize]);
  const totalBuildPoints = useMemo(
    () =>
      deckCounts.one +
      deckCounts.two * 2 +
      deckCounts.three * 3 +
      deckCounts.vp * 4 +
      deckCounts.gt * 6,
    [
      deckCounts.gt,
      deckCounts.one,
      deckCounts.three,
      deckCounts.two,
      deckCounts.vp,
    ],
  );
  const stolenScore = useMemo(
    () =>
      stolenOne +
      stolenTwo * 2 +
      stolenThree * 3 +
      stolenFour * 4 +
      stolenSix * 6 +
      maturedVacheron * 3,
    [maturedVacheron, stolenFour, stolenOne, stolenSix, stolenThree, stolenTwo],
  );
  const stolenCount = useMemo(
    () =>
      stolenOne +
      stolenTwo +
      stolenThree +
      stolenFour +
      stolenSix +
      stolenVacheron,
    [stolenFour, stolenOne, stolenSix, stolenThree, stolenTwo, stolenVacheron],
  );
  const removedTotal = useMemo(
    () => deckSize - remainingCardsInput - stolenCount,
    [deckSize, remainingCardsInput, stolenCount],
  );
  const derivedRemainingCards = useMemo(
    () =>
      Math.max(
        0,
        deckSize -
          removedOne -
          removedTwo -
          removedThree -
          removedFour -
          removedSix -
          removedVacheron -
          stolenCount,
      ),
    [
      deckSize,
      removedVacheron,
      removedFour,
      removedOne,
      removedSix,
      removedThree,
      removedTwo,
      stolenCount,
    ],
  );

  useEffect(() => {
    setRemainingCardsInput(derivedRemainingCards);
  }, [derivedRemainingCards]);

  const deckIssues = useMemo(() => {
    const issues: string[] = [];
    if (deckSize < 40 || deckSize > 59) {
      issues.push("Deck size must be between 40 and 59 cards.");
    }
    if (targetPoints === null) {
      issues.push("Deck size does not match a valid points bracket.");
    } else if (
      totalBuildPoints !== targetPoints &&
      totalBuildPoints !== targetPoints + 1
    ) {
      issues.push(
        `Deck must total ${targetPoints} or ${targetPoints + 1} construction points (currently ${totalBuildPoints}).`,
      );
    }
    if (rawZeroCount < 0) {
      issues.push("Total cards must be at least the sum of non-zero cards.");
    }
    if (rawTwoCount < 0) {
      issues.push("LTD count cannot exceed 2 pointers.");
    }
    if (rawThreeCount < 0) {
      issues.push("GFI + Vacheron count cannot exceed 3 pointers.");
    }
    CARD_TYPES.forEach((card) => {
      if (card.max !== undefined && deckCounts[card.id] > card.max) {
        issues.push(
          `Deck can contain at most ${card.max} ${card.label} cards.`,
        );
      }
    });
    return issues;
  }, [
    deckCounts,
    deckSize,
    rawThreeCount,
    rawTwoCount,
    rawZeroCount,
    targetPoints,
    totalBuildPoints,
  ]);

  const deckStealCounts = useMemo(
    () => ({
      zero: computedDeckCounts.zero,
      one: computedDeckCounts.one + computedDeckCounts.L,
      two: computedDeckCounts.two + computedDeckCounts.G,
      three: computedDeckCounts.three,
      vp: computedDeckCounts.vp,
      gt: computedDeckCounts.gt,
      v: computedDeckCounts.V,
    }),
    [computedDeckCounts],
  );

  const removedZero = useMemo(
    () =>
      removedTotal -
      removedOne -
      removedTwo -
      removedThree -
      removedFour -
      removedSix -
      removedVacheron,
    [
      removedFour,
      removedOne,
      removedSix,
      removedThree,
      removedTotal,
      removedTwo,
      removedVacheron,
    ],
  );

  const removalIssues = useMemo(() => {
    const issues: string[] = [];
    if (remainingCardsInput < 0) {
      issues.push("Remaining cards cannot be negative.");
    }
    if (remainingCardsInput > deckSize - stolenCount) {
      issues.push("Remaining cards exceed deck size minus stolen cards.");
    }
    if (removedZero < 0) {
      issues.push(
        "Remaining cards are too high for removed 1/2/3/4/6/V point cards.",
      );
    }
    if (removedZero > deckStealCounts.zero) {
      issues.push("Removed 0 point cards exceeds deck count.");
    }
    if (removedOne + stolenOne > deckStealCounts.one) {
      issues.push("Removed + stolen 1 point cards exceeds deck count.");
    }
    if (removedTwo + stolenTwo > deckStealCounts.two) {
      issues.push("Removed + stolen 2 point cards exceeds deck count.");
    }
    if (removedThree + stolenThree > deckStealCounts.three) {
      issues.push("Removed + stolen 3 point cards exceeds deck count.");
    }
    if (removedFour + stolenFour > deckStealCounts.vp) {
      issues.push("Removed + stolen 4 point cards exceeds deck count.");
    }
    if (removedSix + stolenSix > deckStealCounts.gt) {
      issues.push("Removed + stolen 6 point cards exceeds deck count.");
    }
    if (removedVacheron + stolenVacheron > deckStealCounts.v) {
      issues.push("Removed + stolen Vacherons exceeds deck count.");
    }
    if (maturedVacheron > stolenVacheron) {
      issues.push("Matured Vacherons cannot exceed stolen Vacherons.");
    }
    return issues;
  }, [
    deckSize,
    deckStealCounts,
    remainingCardsInput,
    removedFour,
    removedOne,
    removedSix,
    removedTwo,
    removedThree,
    removedZero,
    removedVacheron,
    maturedVacheron,
    stolenCount,
    stolenFour,
    stolenOne,
    stolenSix,
    stolenTwo,
    stolenThree,
    stolenVacheron,
  ]);

  const remainingCounts = useMemo(
    () => ({
      zero: deckStealCounts.zero - removedZero,
      one: deckStealCounts.one - removedOne - stolenOne,
      two: deckStealCounts.two - removedTwo - stolenTwo,
      three: deckStealCounts.three - removedThree - stolenThree,
      vp: deckStealCounts.vp - removedFour - stolenFour,
      gt: deckStealCounts.gt - removedSix - stolenSix,
      v: deckStealCounts.v - removedVacheron - stolenVacheron,
    }),
    [
      deckStealCounts,
      removedVacheron,
      removedFour,
      removedOne,
      removedSix,
      removedTwo,
      removedThree,
      removedZero,
      stolenVacheron,
      stolenFour,
      stolenOne,
      stolenSix,
      stolenTwo,
      stolenThree,
    ],
  );

  const remainingCountsList = useMemo(
    () => [
      remainingCounts.zero,
      remainingCounts.one,
      remainingCounts.two,
      remainingCounts.three,
      remainingCounts.vp,
      remainingCounts.gt,
      remainingCounts.v,
    ],
    [remainingCounts],
  );

  const remainingCards = useMemo(
    () => remainingCountsList.reduce((total, count) => total + count, 0),
    [remainingCountsList],
  );
  const maxMcllCount = useMemo(
    () => Math.max(0, remainingCounts.three),
    [remainingCounts.three],
  );
  const maxClicksSpare = useMemo(() => Math.max(0, seeCount), [seeCount]);

  useEffect(() => {
    setMcllCount((prev) => clampWithin(prev, maxMcllCount));
  }, [maxMcllCount]);

  useEffect(() => {
    setClicksSpare((prev) => clampWithin(prev, maxClicksSpare));
  }, [maxClicksSpare]);

  useEffect(() => {
    if (maxMcllCount === 0) {
      setClicksSpare(0);
      setTakeRequiresClick(false);
    }
  }, [maxMcllCount]);

  const expectedSteals = useMemo(() => {
    if (deckIssues.length > 0 || removalIssues.length > 0) return null;
    const values = [0, 1, 2, 3, 4, 6, 0];
    return expectedStealsToReach(remainingCountsList, stolenScore, values, 7);
  }, [
    deckIssues.length,
    removalIssues.length,
    remainingCountsList,
    stolenScore,
  ]);

  useEffect(() => {
    if (!takePinned) {
      setTakeCount(seeCount);
      return;
    }
    if (takeCount > seeCount) {
      setTakeCount(seeCount);
    } else if (takeCount === seeCount) {
      setTakePinned(false);
    }
  }, [seeCount, takeCount, takePinned]);

  const accessData = useMemo(() => {
    const chooseMemo = new Map<string, bigint>();
    const choose = (n: number, k: number) => {
      if (k < 0 || k > n) return 0n;
      const key = `${n}|${k}`;
      const cached = chooseMemo.get(key);
      if (cached !== undefined) return cached;
      let kk = k;
      if (kk > n - kk) kk = n - kk;
      let result = 1n;
      for (let i = 1; i <= kk; i += 1) {
        result = (result * BigInt(n - kk + i)) / BigInt(i);
      }
      chooseMemo.set(key, result);
      return result;
    };

    const see = clampNonNegative(seeCount);
    const take = clampNonNegative(takeCount);
    const totalCardsAvailable = remainingCountsList.reduce(
      (total, count) => total + count,
      0,
    );
    if (see === 0) {
      return { probs: [1], maxGain: 0, totalCards: totalCardsAvailable };
    }
    if (take > see || see > totalCardsAvailable) {
      return { probs: [], maxGain: 0, totalCards: totalCardsAvailable };
    }
    const maxMcll = Math.min(maxMcllCount, clampNonNegative(mcllCount));
    const normalThree = Math.max(0, remainingCounts.three - maxMcll);
    const counts = {
      six: remainingCounts.gt,
      four: remainingCounts.vp,
      three: normalThree,
      two: remainingCounts.two,
      one: remainingCounts.one,
      zero: remainingCounts.zero + remainingCounts.v,
      mcll: maxMcll,
    };
    const maxGain = take * 6;
    const probs = Array(maxGain + 1).fill(0);
    const totalWays = choose(totalCardsAvailable, see);
    const scale = 1_000_000n;
    const clickBudget = clampNonNegative(clicksSpare);
    const useClicks = maxMcll > 0 && takeRequiresClick;

    const sumTop = (takeBudget: number, seenCounts: typeof counts) => {
      let remainingBudget = takeBudget;
      let sum = 0;
      const takeFrom = (value: number, count: number) => {
        if (remainingBudget <= 0 || count <= 0) return;
        const takeCount = Math.min(count, remainingBudget);
        sum += takeCount * value;
        remainingBudget -= takeCount;
      };
      takeFrom(6, seenCounts.six);
      takeFrom(4, seenCounts.four);
      takeFrom(3, seenCounts.three);
      takeFrom(2, seenCounts.two);
      takeFrom(1, seenCounts.one);
      return sum;
    };

    for (let s6 = 0; s6 <= Math.min(counts.six, see); s6 += 1) {
      for (let s4 = 0; s4 <= Math.min(counts.four, see - s6); s4 += 1) {
        for (let s3 = 0; s3 <= Math.min(counts.three, see - s6 - s4); s3 += 1) {
          for (
            let s2 = 0;
            s2 <= Math.min(counts.two, see - s6 - s4 - s3);
            s2 += 1
          ) {
            for (
              let s1 = 0;
              s1 <= Math.min(counts.one, see - s6 - s4 - s3 - s2);
              s1 += 1
            ) {
              for (
                let sm = 0;
                sm <= Math.min(counts.mcll, see - s6 - s4 - s3 - s2 - s1);
                sm += 1
              ) {
                const seenSoFar = s6 + s4 + s3 + s2 + s1 + sm;
                const s0 = see - seenSoFar;
                if (s0 < 0 || s0 > counts.zero) continue;
                const ways =
                  choose(counts.six, s6) *
                  choose(counts.four, s4) *
                  choose(counts.three, s3) *
                  choose(counts.two, s2) *
                  choose(counts.one, s1) *
                  choose(counts.mcll, sm) *
                  choose(counts.zero, s0);
                if (ways === 0n) continue;

                const positiveCount = s6 + s4 + s3 + s2 + s1;
                const sumAllPositive = s6 * 6 + s4 * 4 + s3 * 3 + s2 * 2 + s1;
                let best = 0;

                if (useClicks) {
                  const effectiveTake = Math.min(take, clickBudget);
                  if (effectiveTake > 0) {
                    const totalThree = s3 + sm;
                    const base =
                      effectiveTake >= positiveCount + sm
                        ? sumAllPositive + sm * 3
                        : sumTop(effectiveTake, {
                            ...counts,
                            six: s6,
                            four: s4,
                            three: totalThree,
                            two: s2,
                            one: s1,
                          });
                    best = base;
                  }
                } else {
                  const maxMcllTake = Math.min(sm, clickBudget, take);
                  for (let m = 0; m <= maxMcllTake; m += 1) {
                    const remainingBudget = take - m;
                    if (remainingBudget < 0) continue;
                    const base =
                      remainingBudget >= positiveCount
                        ? sumAllPositive
                        : sumTop(remainingBudget, {
                            ...counts,
                            six: s6,
                            four: s4,
                            three: s3,
                            two: s2,
                            one: s1,
                          });
                    const total = base + m * 3;
                    if (total > best) best = total;
                  }
                }

                if (best > maxGain) continue;
                probs[best] +=
                  Number((ways * scale) / totalWays) / Number(scale);
              }
            }
          }
        }
      }
    }

    return { probs, maxGain, totalCards: totalCardsAvailable };
  }, [
    clicksSpare,
    maxMcllCount,
    mcllCount,
    remainingCards,
    remainingCounts,
    remainingCountsList,
    seeCount,
    takeCount,
    takeRequiresClick,
  ]);

  const multiAccessHistogram = useMemo(() => {
    if (accessData.probs.length === 0) return [];
    const winNeeded = Math.max(0, 7 - stolenScore);
    const maxGain = accessData.maxGain;
    const probAtLeast = (minGain: number) => {
      if (minGain <= 0) return 1;
      if (minGain > maxGain) return 0;
      let sum = 0;
      for (let g = minGain; g <= maxGain; g += 1) {
        sum += accessData.probs[g] ?? 0;
      }
      return sum;
    };
    if (winNeeded <= 1) {
      return [{ label: "W", prob: probAtLeast(winNeeded) }];
    }
    const items: { label: string; prob: number }[] = [];
    const upper = Math.min(winNeeded - 1, maxGain);
    for (let gain = 1; gain <= upper; gain += 1) {
      items.push({ label: `${gain}+`, prob: probAtLeast(gain) });
    }
    items.push({ label: "W", prob: probAtLeast(winNeeded) });
    return items;
  }, [accessData, stolenScore]);

  const noWinProb = useMemo(() => {
    if (accessData.probs.length === 0) return null;
    const winNeeded = Math.max(0, 7 - stolenScore);
    const maxGain = accessData.maxGain;
    if (winNeeded <= 0) return 0;
    if (winNeeded > maxGain) return 1;
    let winProb = 0;
    for (let g = winNeeded; g <= maxGain; g += 1) {
      winProb += accessData.probs[g] ?? 0;
    }
    return Math.max(0, 1 - winProb);
  }, [accessData, stolenScore]);

  const deferredDeckSize = useDeferredValue(deckSize);
  const deferredTargetPoints = useDeferredValue(targetPoints);
  const deferredAllowVP = useDeferredValue(allowVP);
  const deferredAllowGT = useDeferredValue(allowGT);
  const deferredAllowL = useDeferredValue(allowL);
  const deferredAllowG = useDeferredValue(allowG);
  const deferredAllowV = useDeferredValue(allowV);
  const deferredLimitHigh = useDeferredValue(limitHighPointers);
  const deferredMaxHigh = useDeferredValue(maxHighPointers);
  const stealsCacheRef = useRef(new Map<string, number>());
  const isTableStale =
    deferredDeckSize !== deckSize ||
    deferredTargetPoints !== targetPoints ||
    deferredAllowVP !== allowVP ||
    deferredAllowGT !== allowGT ||
    deferredAllowL !== allowL ||
    deferredAllowG !== allowG ||
    deferredAllowV !== allowV ||
    deferredLimitHigh !== limitHighPointers ||
    deferredMaxHigh !== maxHighPointers;
  const [showRecalc, setShowRecalc] = useState(false);

  useEffect(() => {
    if (!isTableStale) {
      setShowRecalc(false);
      return;
    }
    const timer = window.setTimeout(() => setShowRecalc(true), 75);
    return () => window.clearTimeout(timer);
  }, [isTableStale]);

  const deckTableRows = useMemo(() => {
    if (deferredTargetPoints === null) return [];
    if (deferredDeckSize < 40 || deferredDeckSize > 59) return [];
    const rows: DeckRow[] = [];
    const targetOptions = new Set([
      deferredTargetPoints,
      deferredTargetPoints + 1,
    ]);
    const maxVP = deferredAllowVP ? 3 : 0;
    const maxGT = deferredAllowGT ? 1 : 0;
    for (let gt = 0; gt <= maxGT; gt += 1) {
      for (let vp = 0; vp <= maxVP; vp += 1) {
        for (let one = 0; one <= deferredDeckSize; one += 1) {
          const maxTwo = deferredDeckSize - one - vp - gt;
          if (maxTwo < 0) break;
          for (let two = 0; two <= maxTwo; two += 1) {
            const maxThree = deferredDeckSize - one - two - vp - gt;
            for (let three = 0; three <= maxThree; three += 1) {
              const zero = deferredDeckSize - one - two - three - vp - gt;
              if (zero < 0) continue;
              const points = one + two * 2 + three * 3 + vp * 4 + gt * 6;
              if (!targetOptions.has(points)) continue;
              const maxL = deferredAllowL ? Math.min(3, two) : 0;
              const maxG = deferredAllowG ? Math.min(3, three) : 0;
              for (let l = 0; l <= maxL; l += 1) {
                for (let g = 0; g <= maxG; g += 1) {
                  const maxV = deferredAllowV ? Math.min(3, three - g) : 0;
                  for (let v = 0; v <= maxV; v += 1) {
                    if (deferredLimitHigh) {
                      const highCount = three + vp + gt;
                      if (highCount > deferredMaxHigh) continue;
                    }
                    const baseThree = three - g - v;
                    const stealCounts = [
                      zero,
                      one + l,
                      two - l + g,
                      baseThree,
                      vp,
                      gt,
                      v,
                    ];
                    const key = stealCounts.join("|");
                    const cached = stealsCacheRef.current.get(key);
                    const stealsToWin =
                      cached ??
                      expectedStealsToReach(
                        stealCounts,
                        0,
                        [0, 1, 2, 3, 4, 6, 0],
                        7,
                      );
                    if (cached === undefined) {
                      stealsCacheRef.current.set(key, stealsToWin);
                    }
                    rows.push({
                      one,
                      two,
                      three,
                      vp,
                      gt,
                      l,
                      g,
                      v,
                      zero,
                      points,
                      stealsToWin,
                    });
                  }
                }
              }
            }
          }
        }
      }
    }
    return rows.sort((a, b) => b.stealsToWin - a.stealsToWin);
  }, [
    deferredAllowG,
    deferredAllowGT,
    deferredAllowL,
    deferredAllowVP,
    deferredAllowV,
    deferredDeckSize,
    deferredLimitHigh,
    deferredMaxHigh,
    deferredTargetPoints,
  ]);

  const handleCountChange = (
    setter: Dispatch<SetStateAction<Record<string, number>>>,
    id: string,
    value: number,
  ) => {
    setter((prev) => ({ ...prev, [id]: clampNonNegative(value) }));
  };

  const handleTotalCardsChange = (value: number) => {
    setTotalCards(clampNonNegative(value));
  };

  const loadDeckFromRow = (row: DeckRow) => {
    setTotalCards(totalCards);
    setDeckCounts((prev) => ({
      ...prev,
      one: row.one,
      two: row.two,
      three: row.three,
      L: row.l,
      G: row.g,
      V: row.v,
      vp: row.vp,
      gt: row.gt,
    }));
    setRemovedOne(0);
    setRemovedTwo(0);
    setRemovedThree(0);
    setRemovedFour(0);
    setRemovedSix(0);
    setRemovedVacheron(0);
    setStolenOne(0);
    setStolenTwo(0);
    setStolenThree(0);
    setStolenFour(0);
    setStolenSix(0);
    setStolenVacheron(0);
    setMaturedVacheron(0);
    setRemainingCardsInput(totalCards);
  };

  const showRemovedOne =
    deckStealCounts.one > 0 || removedOne > 0 || stolenOne > 0;
  const showRemovedTwo =
    deckStealCounts.two > 0 || removedTwo > 0 || stolenTwo > 0;
  const showRemovedThree =
    deckStealCounts.three > 0 || removedThree > 0 || stolenThree > 0;
  const showRemovedFour =
    deckStealCounts.vp > 0 || removedFour > 0 || stolenFour > 0;
  const showRemovedSix =
    deckStealCounts.gt > 0 || removedSix > 0 || stolenSix > 0;
  const showRemovedVacheron =
    deckStealCounts.v > 0 ||
    removedVacheron > 0 ||
    stolenVacheron > 0 ||
    maturedVacheron > 0;

  const maxRemovedOne = Math.max(0, deckStealCounts.one - stolenOne);
  const maxRemovedTwo = Math.max(0, deckStealCounts.two - stolenTwo);
  const maxRemovedThree = Math.max(0, deckStealCounts.three - stolenThree);
  const maxRemovedFour = Math.max(0, deckStealCounts.vp - stolenFour);
  const maxRemovedSix = Math.max(0, deckStealCounts.gt - stolenSix);
  const maxRemovedVacheron = Math.max(0, deckStealCounts.v - stolenVacheron);

  const maxStolenOne = Math.max(0, deckStealCounts.one - removedOne);
  const maxStolenTwo = Math.max(0, deckStealCounts.two - removedTwo);
  const maxStolenThree = Math.max(0, deckStealCounts.three - removedThree);
  const maxStolenFour = Math.max(0, deckStealCounts.vp - removedFour);
  const maxStolenSix = Math.max(0, deckStealCounts.gt - removedSix);
  const maxStolenVacheron = Math.max(0, deckStealCounts.v - removedVacheron);
  const maxMaturedVacheron = Math.max(0, stolenVacheron);

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <div className="mx-auto max-w-6xl px-4 py-4">
        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded border border-slate-200 bg-white p-3 text-sm">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Deck
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <span>Total Cards</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={59}
                  value={totalCards}
                  onChange={(event) =>
                    handleTotalCardsChange(Number(event.target.value))
                  }
                />
              </div>
              <div className="h-px bg-slate-200" />
              <div className="flex items-center justify-between gap-2">
                <span>1 Pointers</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={59}
                  value={deckCounts.one}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "one",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>2 Pointers</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={59}
                  value={deckCounts.two}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "two",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>3 Pointers</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={59}
                  value={deckCounts.three}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "three",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Vanity Project</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={3}
                  value={deckCounts.vp}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "vp",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Government Takeover</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={1}
                  value={deckCounts.gt}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "gt",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="h-px bg-slate-200" />
              <div className="flex items-center justify-between gap-2">
                <span>Project Vacheron</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={3}
                  value={deckCounts.V}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "V",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Let Them Dream</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={3}
                  value={deckCounts.L}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "L",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Global Food Initiative</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={3}
                  value={deckCounts.G}
                  onChange={(event) =>
                    handleCountChange(
                      setDeckCounts,
                      "G",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
            </div>
            <div className="mt-3 grid gap-1 text-xs text-slate-600">
              <div className="flex items-center justify-between">
                <span>Construction points</span>
                <span className="tabular-nums">{totalBuildPoints}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Target points</span>
                <span className="tabular-nums">{targetPoints ?? "-"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Derived 0 cards</span>
                <span className="tabular-nums">{zeroCount}</span>
              </div>
            </div>
            <div className="mt-2 grid gap-1 text-xs">
              {deckIssues.length === 0 ? (
                <p className="text-emerald-700">Deck valid.</p>
              ) : (
                deckIssues.map((issue) => (
                  <p className="text-red-700" key={issue}>
                    {issue}
                  </p>
                ))
              )}
            </div>
          </section>

          <section className="rounded border border-slate-200 bg-white p-3 text-sm">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Game
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <span>Remaining Cards</span>
                <input
                  className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  type="number"
                  min={0}
                  max={59}
                  value={remainingCardsInput}
                  onChange={(event) =>
                    setRemainingCardsInput(
                      clampNonNegative(Number(event.target.value)),
                    )
                  }
                />
              </div>
              <div className="h-px bg-slate-200" />
              {showRemovedOne ? (
                <div className="flex items-center justify-between gap-2">
                  <span>1 Points Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedOne}
                    value={removedOne}
                    onChange={(event) =>
                      setRemovedOne(
                        clampWithin(Number(event.target.value), maxRemovedOne),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedTwo ? (
                <div className="flex items-center justify-between gap-2">
                  <span>2 Points Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedTwo}
                    value={removedTwo}
                    onChange={(event) =>
                      setRemovedTwo(
                        clampWithin(Number(event.target.value), maxRemovedTwo),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedThree ? (
                <div className="flex items-center justify-between gap-2">
                  <span>3 Points Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedThree}
                    value={removedThree}
                    onChange={(event) =>
                      setRemovedThree(
                        clampWithin(
                          Number(event.target.value),
                          maxRemovedThree,
                        ),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedFour ? (
                <div className="flex items-center justify-between gap-2">
                  <span>4 Points Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedFour}
                    value={removedFour}
                    onChange={(event) =>
                      setRemovedFour(
                        clampWithin(Number(event.target.value), maxRemovedFour),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedSix ? (
                <div className="flex items-center justify-between gap-2">
                  <span>6 Points Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedSix}
                    value={removedSix}
                    onChange={(event) =>
                      setRemovedSix(
                        clampWithin(Number(event.target.value), maxRemovedSix),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedVacheron ? (
                <div className="flex items-center justify-between gap-2">
                  <span>Project Vacherons Removed</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxRemovedVacheron}
                    value={removedVacheron}
                    onChange={(event) =>
                      setRemovedVacheron(
                        clampWithin(
                          Number(event.target.value),
                          maxRemovedVacheron,
                        ),
                      )
                    }
                  />
                </div>
              ) : null}
              <div className="h-px bg-slate-200" />
              {showRemovedOne ? (
                <div className="flex items-center justify-between gap-2">
                  <span>1 Points Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenOne}
                    value={stolenOne}
                    onChange={(event) =>
                      setStolenOne(
                        clampWithin(Number(event.target.value), maxStolenOne),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedTwo ? (
                <div className="flex items-center justify-between gap-2">
                  <span>2 Points Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenTwo}
                    value={stolenTwo}
                    onChange={(event) =>
                      setStolenTwo(
                        clampWithin(Number(event.target.value), maxStolenTwo),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedThree ? (
                <div className="flex items-center justify-between gap-2">
                  <span>3 Points Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenThree}
                    value={stolenThree}
                    onChange={(event) =>
                      setStolenThree(
                        clampWithin(Number(event.target.value), maxStolenThree),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedFour ? (
                <div className="flex items-center justify-between gap-2">
                  <span>4 Points Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenFour}
                    value={stolenFour}
                    onChange={(event) =>
                      setStolenFour(
                        clampWithin(Number(event.target.value), maxStolenFour),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedSix ? (
                <div className="flex items-center justify-between gap-2">
                  <span>6 Points Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenSix}
                    value={stolenSix}
                    onChange={(event) =>
                      setStolenSix(
                        clampWithin(Number(event.target.value), maxStolenSix),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedVacheron ? (
                <div className="flex items-center justify-between gap-2">
                  <span>Project Vacherons Stolen</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxStolenVacheron}
                    value={stolenVacheron}
                    onChange={(event) =>
                      setStolenVacheron(
                        clampWithin(
                          Number(event.target.value),
                          maxStolenVacheron,
                        ),
                      )
                    }
                  />
                </div>
              ) : null}
              {showRemovedVacheron ? (
                <div className="flex items-center justify-between gap-2">
                  <span>Project Vacherons Matured</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={maxMaturedVacheron}
                    value={maturedVacheron}
                    onChange={(event) =>
                      setMaturedVacheron(
                        clampWithin(
                          Number(event.target.value),
                          maxMaturedVacheron,
                        ),
                      )
                    }
                  />
                </div>
              ) : null}
            </div>
            <div className="mt-3 grid gap-1 text-xs text-slate-600">
              <div className="flex items-center justify-between">
                <span>Removed 0s (derived)</span>
                <span className="tabular-nums">{removedZero}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Remaining cards</span>
                <span className="tabular-nums">{remainingCards}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Current stolen score</span>
                <span className="tabular-nums">{stolenScore}</span>
              </div>
            </div>
            <div className="mt-2 grid gap-1 text-xs">
              {removalIssues.length === 0 ? (
                <p className="text-emerald-700">Game state valid.</p>
              ) : (
                removalIssues.map((issue) => (
                  <p className="text-red-700" key={issue}>
                    {issue}
                  </p>
                ))
              )}
            </div>
          </section>

          <section className="rounded border border-slate-200 bg-white p-3 text-sm">
            <div className="mt-3">
              {expectedSteals === null ? (
                <p className="text-sm text-red-700">Fix issues to compute.</p>
              ) : expectedSteals === Infinity ? (
                <p className="text-sm text-red-700">Not enough points left.</p>
              ) : (
                <>
                  <div className="text-3xl font-semibold tabular-nums">
                    {expectedSteals.toFixed(3)}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Remaining cards:{" "}
                    <span className="tabular-nums">{remainingCards}</span>
                  </div>
                </>
              )}
            </div>
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1">
                    <span>See</span>
                    <input
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                      type="number"
                      min={0}
                      max={remainingCards}
                      value={seeCount}
                      onChange={(event) =>
                        setSeeCount(
                          clampNonNegative(Number(event.target.value)),
                        )
                      }
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    <span>Take</span>
                    <input
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                      type="number"
                      min={0}
                      max={seeCount}
                      value={takeCount}
                      onChange={(event) => {
                        const nextValue = clampNonNegative(
                          Number(event.target.value),
                        );
                        setTakePinned(nextValue !== seeCount);
                        setTakeCount(nextValue);
                      }}
                    />
                  </label>
                  <div className="w-full"></div>
                  <label className="flex items-center gap-1">
                    <span>MCLL/IPs</span>
                    <input
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                      type="number"
                      min={0}
                      max={maxMcllCount}
                      value={mcllCount}
                      onChange={(event) =>
                        setMcllCount(
                          clampWithin(Number(event.target.value), maxMcllCount),
                        )
                      }
                    />
                  </label>
                  {mcllCount > 0 ? (
                    <label className="flex items-center gap-1">
                      <span>Clicks Spare</span>
                      <input
                        className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                        type="number"
                        min={0}
                        max={maxClicksSpare}
                        value={clicksSpare}
                        onChange={(event) =>
                          setClicksSpare(
                            clampWithin(
                              Number(event.target.value),
                              maxClicksSpare,
                            ),
                          )
                        }
                      />
                    </label>
                  ) : null}
                  {mcllCount > 0 ? (
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={takeRequiresClick}
                        onChange={(event) =>
                          setTakeRequiresClick(event.target.checked)
                        }
                      />
                      <span>Take requires click</span>
                    </label>
                  ) : null}
                </div>
              </div>
              {seeCount === 0 ? (
                <div className="mt-2 text-xs text-slate-500">
                  Enter how many cards you can see.
                </div>
              ) : seeCount > remainingCards ? (
                <div className="mt-2 text-xs text-red-700">
                  Cannot see more than {remainingCards} cards.
                </div>
              ) : takeCount > seeCount ? (
                <div className="mt-2 text-xs text-red-700">
                  Take cannot exceed See.
                </div>
              ) : (
                <div className="mt-3 grid gap-2 text-xs">
                  <div className="text-right text-slate-600">
                    Whiff{" "}
                    <span className="tabular-nums">
                      {((accessData.probs[0] ?? 0) * 100).toFixed(2)}%
                    </span>
                  </div>
                  {multiAccessHistogram
                    .filter((item) => item.prob > 0)
                    .map((item) => (
                      <div className="flex items-center gap-2" key={item.label}>
                        <div className="w-8 text-right font-semibold">
                          {item.label}
                        </div>
                        <div className="h-2 flex-1 rounded bg-slate-200">
                          <div
                            className="h-2 rounded bg-slate-600"
                            style={{
                              width: `${(item.prob * 100).toFixed(2)}%`,
                            }}
                          />
                        </div>
                        <div className="w-14 text-right tabular-nums">
                          {(item.prob * 100).toFixed(2)}%
                        </div>
                      </div>
                    ))}
                  {multiAccessHistogram.some(
                    (item) => item.label === "W" && item.prob > 0,
                  ) && noWinProb !== null ? (
                    <div className="text-right text-slate-600">
                      No Win{" "}
                      <span className="tabular-nums">
                        {(noWinProb * 100).toFixed(2)}%
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="mt-4 rounded border border-slate-200 bg-white p-3 text-sm">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Valid Decks (Total {totalCards})
          </div>
          <div className="mt-2 grid gap-4 text-xs lg:grid-cols-[220px_auto]">
            <div className="grid gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowVP}
                  onChange={(event) => setAllowVP(event.target.checked)}
                />
                Vanity Project
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowGT}
                  onChange={(event) => setAllowGT(event.target.checked)}
                />
                Government Takeover
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowV}
                  onChange={(event) => setAllowV(event.target.checked)}
                />
                Project Vacheron
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowL}
                  onChange={(event) => setAllowL(event.target.checked)}
                />
                Let Them Dream
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowG}
                  onChange={(event) => setAllowG(event.target.checked)}
                />
                Global Food Initiative
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={limitHighPointers}
                  onChange={(event) =>
                    setLimitHighPointers(event.target.checked)
                  }
                />
                Limit 3+ pointers
              </label>
              {limitHighPointers ? (
                <label className="flex items-center gap-2">
                  <span>Max</span>
                  <input
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                    type="number"
                    min={0}
                    max={59}
                    value={maxHighPointers}
                    onChange={(event) =>
                      setMaxHighPointers(
                        clampNonNegative(Number(event.target.value)),
                      )
                    }
                  />
                </label>
              ) : null}
              <div className="text-xs text-slate-600">
                {deckTableRows.length} valid constructions.
              </div>
            </div>
            <div className="relative max-h-[320px] w-max overflow-auto rounded border border-slate-200">
              {showRecalc ? (
                <div className="sticky top-0 z-10 border-b border-slate-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                  Recalculating…
                </div>
              ) : null}
              {deckTableRows.length === 0 ? (
                <div
                  className={
                    showRecalc
                      ? "p-3 text-xs text-slate-400"
                      : "p-3 text-xs text-slate-600"
                  }
                >
                  No valid constructions for this deck size and filters.
                </div>
              ) : (
                <table
                  className={
                    showRecalc
                      ? "w-fit border-collapse text-xs opacity-50"
                      : "w-fit border-collapse text-xs"
                  }
                >
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                        1
                      </th>
                      <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                        2
                      </th>
                      <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                        3
                      </th>
                      {allowVP ? (
                        <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                          VP
                        </th>
                      ) : null}
                      {allowGT ? (
                        <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                          GT
                        </th>
                      ) : null}
                      {allowV ? (
                        <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                          V
                        </th>
                      ) : null}
                      {allowL ? (
                        <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                          LTD
                        </th>
                      ) : null}
                      {allowG ? (
                        <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                          GFI
                        </th>
                      ) : null}
                      <th className="border-b border-slate-200 px-1 py-1 text-left w-[3ch]">
                        Pts
                      </th>
                      <th className="border-b border-slate-200 px-1 py-1 text-left">
                        Density
                      </th>
                      <th className="border-b border-slate-200 px-1 py-1 text-left">
                        Load
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deckTableRows.map((row, index) => (
                      <tr
                        key={`${row.one}-${row.two}-${row.three}-${row.vp}-${row.gt}-${row.l}-${row.g}-${row.v}-${row.zero}-${row.points}-${index}`}
                      >
                        <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                          {row.one}
                        </td>
                        <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                          {row.two}
                        </td>
                        <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                          {row.three}
                        </td>
                        {allowVP ? (
                          <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                            {row.vp}
                          </td>
                        ) : null}
                        {allowGT ? (
                          <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                            {row.gt}
                          </td>
                        ) : null}
                        {allowV ? (
                          <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                            {row.v}
                          </td>
                        ) : null}
                        {allowL ? (
                          <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                            {row.l}
                          </td>
                        ) : null}
                        {allowG ? (
                          <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                            {row.g}
                          </td>
                        ) : null}
                        <td className="border-b border-slate-100 px-1 py-1 w-[3ch]">
                          {row.points}
                        </td>
                        <td className="border-b border-slate-100 px-1 py-1">
                          {row.stealsToWin === Infinity
                            ? "∞"
                            : row.stealsToWin.toFixed(3)}
                        </td>
                        <td className="border-b border-slate-100 px-1 py-1">
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() => loadDeckFromRow(row)}
                          >
                            Load
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default App;
